'use client'

import moment from 'moment-timezone'
import { useState } from 'react'
import useInterval from '@/hooks/shared/useInterval'

/**
 * Validade do cupom para os cartões e o resumo do cupom (substitui o `helpers/verifyCoupon` e a contagem que cada
 * componente repetia). A data da API é lida com o horário de parede de São Paulo, como o legado fazia.
 */

const TIMEZONE = 'America/Sao_Paulo'
const DAY = 24 * 60 * 60 * 1000
const MONTHS = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
]

export interface ICouponExpiration {
    /** "Expira em HH:mm:ss" (menos de 24 horas), "Válido até DD de Mês" ou "Cupom expirado !". */
    descriptionText: string
    /** Milissegundos até o fim da validade (negativo depois dela). */
    timeLeft: number
    /** Percentual decorrido das últimas 24 horas (para a barra de progresso). */
    progress: number
    /** Faltam menos de 24 horas: mostra a contagem e a barra. */
    isExpiringSoon: boolean
}

const NONE: ICouponExpiration = { descriptionText: '', timeLeft: 0, progress: 0, isExpiringSoon: false }

export function getCouponExpiration(validateEnd: string, now = moment.tz(TIMEZONE)): ICouponExpiration {
    const end = moment.tz(validateEnd, 'UTC').clone().tz(TIMEZONE, true)
    const timeLeft = end.diff(now)
    const progress = Math.max(0, Math.min(100, ((DAY - timeLeft) / DAY) * 100))

    if (timeLeft > 0 && timeLeft < DAY) {
        return {
            descriptionText: `Expira em ${moment.utc(timeLeft).format('HH:mm:ss')}`,
            timeLeft,
            progress,
            isExpiringSoon: true,
        }
    }
    if (timeLeft >= DAY) {
        const day = String(end.date()).padStart(2, '0')
        return {
            descriptionText: `Válido até ${day} de ${MONTHS[end.month()]}`,
            timeLeft,
            progress,
            isExpiringSoon: false,
        }
    }
    return { descriptionText: 'Cupom expirado !', timeLeft, progress, isExpiringSoon: false }
}

/** Validade do cupom recalculada a cada segundo (a cada tique a renderização recalcula); sem data, nada. */
export function useCouponExpiration(validateEnd: string | null | undefined): ICouponExpiration {
    const [, setTick] = useState(0)
    useInterval(() => setTick((tick) => tick + 1), validateEnd ? 1000 : null)

    return validateEnd ? getCouponExpiration(validateEnd) : NONE
}
